import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * Rose-gold harmonized PDF quote generator (admin "Devis PDF" button).
 */
const ROSE_GOLD = [184, 151, 126];
const CREAM = [250, 248, 245];
const DARK = [45, 35, 25];
const LIGHT_BORDER = [232, 213, 196];
const WHITE = [255, 255, 255];
const MUTED = [120, 120, 120];
const SIGN_GREEN = [45, 122, 79];
const SIGN_GREEN_BG = [240, 248, 240];

const PACKAGE_FEATURES = [
  "Photobooth autonome — prise en main à distance",
  "1 imprimante incluse",
  "Impressions illimitées qualité photo",
  "Accessoires & props inclus",
  "Galerie digitale",
];

const OPTIONS_LABELS = {
  magnet_50: "Support Photo Aimanté (50 unités)",
  magnet_100: "Support Photo Aimanté (100 unités)",
  backdrop: "Fond pour Photos",
};
const OPTIONS_PRICES = { magnet_50: 50, magnet_100: 80, backdrop: 40 };

function describeOption(optKey, options) {
  const base = OPTIONS_LABELS[optKey] || optKey;
  if (optKey === "magnet_50" || optKey === "magnet_100") {
    const size = (options?.magnet_size || "").toLowerCase();
    const color = (options?.magnet_color || "").toLowerCase();
    const sizeLabel = size === "10x15" ? "10×15 cm" : size === "5x15" ? "5×15 cm" : "";
    const colorLabel = color === "blanc" ? "Blanc" : color === "noir" ? "Noir" : "";
    const extras = [sizeLabel, colorLabel].filter(Boolean).join(" — ");
    return extras ? `${base} — ${extras}` : base;
  }
  return base;
}

function formatDateSafe(value, fallback = "—") {
  if (!value) return fallback;
  try {
    if (typeof value === "string" && value.length >= 10) {
      const dt = value.includes("T") ? parseISO(value) : parseISO(`${value}T00:00:00`);
      return format(dt, "dd/MM/yyyy", { locale: fr });
    }
    return format(new Date(value), "dd/MM/yyyy", { locale: fr });
  } catch {
    return fallback;
  }
}

export const generateQuotePDF = async (booking) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();

  // === HEADER ===
  doc.setFillColor(...ROSE_GOLD);
  doc.rect(0, 0, pageW, 40, "F");

  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("DEVIS", 15, 22);

  const quoteNumber = `D${new Date().getFullYear()}${(booking.id || "XXXX").substring(0, 4).toUpperCase()}`;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`N° ${quoteNumber}  •  ${format(new Date(), "dd MMMM yyyy", { locale: fr })}`, 15, 30);

  // Validity badge
  doc.setFillColor(...DARK);
  doc.roundedRect(pageW - 60, 12, 45, 9, 2, 2, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...WHITE);
  doc.text("VALIDE 30 JOURS", pageW - 37.5, 18, { align: "center" });

  // === ÉMETTEUR / CLIENT ===
  let y = 52;
  doc.setTextColor(...ROSE_GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("ÉMETTEUR", 15, y);
  doc.text("DESTINATAIRE", pageW / 2 + 5, y);
  doc.setDrawColor(...LIGHT_BORDER);
  doc.setLineWidth(0.3);
  doc.line(15, y + 1.5, 80, y + 1.5);
  doc.line(pageW / 2 + 5, y + 1.5, pageW - 15, y + 1.5);

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  const issuer = [
    "SelfieBooth Event",
    "44B Rue Gabriel Péri",
    "78210 Saint-Cyr-l'École",
    "+33 6 79 39 34 29",
    "walid.societe.pro@gmail.com",
    "SIRET : 93145736000010",
  ];
  issuer.forEach((line, i) => doc.text(line, 15, y + i * 5));
  const client = [
    booking.customer_name || "",
    booking.customer_email || "",
    booking.customer_phone || "",
    booking.event_location || "",
  ].filter(Boolean);
  client.forEach((line, i) => doc.text(line, pageW / 2 + 5, y + i * 5));

  // === DÉTAILS ÉVÉNEMENT ===
  y = Math.max(y + issuer.length * 5, y + client.length * 5) + 6;
  doc.setFillColor(...CREAM);
  doc.rect(15, y, pageW - 30, 12, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...ROSE_GOLD);
  doc.text("DATE DE L'ÉVÉNEMENT", 19, y + 5);
  doc.text("LIEU", 80, y + 5);
  doc.text("TYPE", 140, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...DARK);
  doc.text(formatDateSafe(booking.event_date), 19, y + 10);
  doc.text(booking.event_location || "—", 80, y + 10, { maxWidth: 55 });
  doc.text(booking.event_type || "—", 140, y + 10);

  // === TABLEAU ===
  y += 18;
  const rows = [];
  const packageLabel = booking.package_name || booking.package_id || "Forfait photobooth";
  const packagePrice = Number(booking.package_price || 0);
  rows.push([
    "Forfait",
    `${packageLabel}\n• ${PACKAGE_FEATURES.join("\n• ")}`,
    `${packagePrice.toFixed(2)} €`,
    "1",
    `${packagePrice.toFixed(2)} €`,
  ]);
  Object.entries(booking.options || {}).forEach(([k, v]) => {
    if (v === true && k in OPTIONS_PRICES) {
      const price = OPTIONS_PRICES[k];
      rows.push([
        "Option",
        describeOption(k, booking.options),
        `${price.toFixed(2)} €`,
        "1",
        `${price.toFixed(2)} €`,
      ]);
    }
  });

  autoTable(doc, {
    startY: y,
    head: [["TYPE", "DÉSIGNATION", "P.U. HT", "QTÉ", "TOTAL HT"]],
    body: rows,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 3, textColor: DARK, lineColor: LIGHT_BORDER, lineWidth: 0.1 },
    headStyles: { fillColor: DARK, textColor: WHITE, fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: CREAM },
    columnStyles: {
      0: { cellWidth: 22 },
      2: { halign: "right", cellWidth: 24 },
      3: { halign: "center", cellWidth: 14 },
      4: { halign: "right", cellWidth: 26 },
    },
    margin: { left: 15, right: 15 },
  });

  const totalY = doc.lastAutoTable.finalY + 6;
  const total = Number(booking.total_price || 0);
  doc.setFillColor(...ROSE_GOLD);
  doc.rect(pageW - 90, totalY, 75, 12, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`TOTAL HT : ${total.toFixed(2)} €`, pageW - 87, totalY + 8);

  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.text("TVA non applicable — article 293 B du CGI", pageW - 15, totalY + 17, { align: "right" });

  // === BON POUR ACCORD ===
  let sigY = totalY + 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...ROSE_GOLD);
  doc.text("BON POUR ACCORD", 15, sigY);
  doc.setDrawColor(...LIGHT_BORDER);
  doc.line(15, sigY + 1.5, 80, sigY + 1.5);
  sigY += 6;

  if (booking.signed || booking.signature_data) {
    doc.setFillColor(...SIGN_GREEN_BG);
    doc.rect(15, sigY, 120, 26, "F");
    doc.setDrawColor(...SIGN_GREEN);
    doc.setLineWidth(0.5);
    doc.rect(15, sigY, 120, 26);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...SIGN_GREEN);
    doc.text("✓ DEVIS ACCEPTÉ ET SIGNÉ", 19, sigY + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(`Par : ${booking.signed_by || "—"}`, 19, sigY + 14);
    const signedDisplay = booking.signed_display || formatDateSafe(booking.signed_at);
    doc.text(`Le : ${signedDisplay}`, 19, sigY + 20);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text("À _________________, le ___/___/______", 15, sigY + 6);
    doc.text("Signature du client précédée de la mention « Bon pour accord »", 15, sigY + 13);
    // Empty box
    doc.setDrawColor(...LIGHT_BORDER);
    doc.setLineWidth(0.3);
    doc.rect(15, sigY + 16, 80, 24);
  }

  // === FOOTER ===
  doc.setFillColor(...DARK);
  doc.rect(0, 277, pageW, 20, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(11);
  doc.text("Merci de nous retourner ce devis signé", pageW / 2, 284, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(215, 200, 185);
  doc.text(
    "SelfieBooth Event — 44B Rue Gabriel Péri, 78210 Saint-Cyr-l'École — SIRET 93145736000010",
    pageW / 2,
    291,
    { align: "center" }
  );

  doc.save(`Devis_SelfieBooth_${quoteNumber}.pdf`);
};

export default generateQuotePDF;
